import DataMap from 'assets/components/dataMap/DataMap';
import { formatCurrency } from 'assets/utils/data/Number';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { keys } from 'lodash';

type RowData = {
  id?: number;
  key: string;
  originalAmount: number;
  retainedAmount: number;
  refundedAmount: number;
};
type TableData = {
  rows: RowData[];
  totalOriginalAmount: number;
  totalRetainedAmount: number;
  totalRefundedAmount: number;
};

//#region Distribution table
type DistributionProps = {
  distributions: Utils.ContractRefundDistribution;
  onRetainedValueChange?(data: { key: string; amount: number; id?: number }): void;
};
type RefundDistributionTableProps = DistributionProps & {
  originalRetainedTotal?: number;
  title?: string;
};
const getDistributionTableData = ({ distributions }: DistributionProps): TableData => {
  const rows = keys(distributions).map((key) => {
    return {
      key: key,
      originalAmount: distributions[key]?.original?.amount ?? 0,
      retainedAmount: distributions[key]?.retained?.amount ?? 0,
      refundedAmount: distributions[key]?.refunded?.amount ?? 0,
    };
  });

  const totalOriginal = rows.reduce((sum, r) => sum + r.originalAmount, 0);
  const totalRetained = rows.reduce((sum, r) => sum + r.retainedAmount, 0);
  const totalRefunded = rows.reduce((sum, r) => sum + r.refundedAmount, 0);

  return {
    rows,
    totalOriginalAmount: totalOriginal,
    totalRetainedAmount: totalRetained,
    totalRefundedAmount: totalRefunded,
  };
};
export const RefundDistributionTable = ({
  title,
  distributions,
  originalRetainedTotal,
  onRetainedValueChange,
}: RefundDistributionTableProps) => {
  const data = getDistributionTableData({
    distributions,
  });

  return (
    <RefundTableBase
      data={data}
      title={title}
      onRetainedValueChange={onRetainedValueChange}
      originalRetainedTotal={originalRetainedTotal}
    />
  );
};
//#endregion

//#region Subfees table
type SubfeeProps = {
  subfees: Utils.ContractRefundSubfee[];
};
type RefundSubfeeTableProps = SubfeeProps & {
  title?: string;
  originalRetainedTotal?: number;
  onRetainedValueChange?(data: { key: string; amount: number; id: number }): void;
};

const getSubfeeTableData = ({ subfees }: SubfeeProps): TableData => {
  const rows = subfees.map((s) => {
    return {
      id: s.subfeeId,
      key: s.name,
      originalAmount: s?.original?.amount ?? 0,
      retainedAmount: s?.retained?.amount ?? 0,
      refundedAmount: s?.refunded?.amount ?? 0,
    };
  });

  const totalOriginal = rows.reduce((sum, r) => sum + r.originalAmount, 0);
  const totalRetained = rows.reduce((sum, r) => sum + r.retainedAmount, 0);
  const totalRefunded = rows.reduce((sum, r) => sum + r.refundedAmount, 0);

  return {
    rows,
    totalOriginalAmount: totalOriginal,
    totalRetainedAmount: totalRetained,
    totalRefundedAmount: totalRefunded,
  };
};

export const RefundSubfeeTable = ({
  title,
  subfees,
  onRetainedValueChange,
  originalRetainedTotal,
}: RefundSubfeeTableProps) => {
  const data = getSubfeeTableData({
    subfees,
  });

  return (
    <RefundTableBase
      data={data}
      title={title}
      onRetainedValueChange={onRetainedValueChange}
      originalRetainedTotal={originalRetainedTotal}
    />
  );
};
//#endregion

const RefundTableBase = ({
  title,
  data,
  originalRetainedTotal,
  onRetainedValueChange,
}: {
  data: TableData;
  title?: string;
  originalRetainedTotal?: number;
  onRetainedValueChange?: RefundDistributionTableProps['onRetainedValueChange'];
}) => {
  const { lang } = getLocales();
  const hasRetainedValueChanged = data.totalRetainedAmount !== originalRetainedTotal && originalRetainedTotal != null;
  return (
    <>
      {title && <h3>{title}</h3>}
      <table className={ThemeTableSimple}>
        <thead>
          <tr>
            <th>{lang.distribution}</th>
            <th style={{ textAlign: 'center' }}>{lang.original}</th>
            <th style={{ textAlign: 'center' }}>{lang.retained}</th>
            <th style={{ textAlign: 'center' }}>{lang.refunded}</th>
          </tr>
        </thead>
        <tbody>
          <DataMap
            data={data.rows}
            render={({ data: row }) => {
              return (
                <tr key={row.key} style={{ height: '50px' }}>
                  <td style={{ fontWeight: 'bold' }}>{row.key}</td>
                  <td style={{ textAlign: 'center' }}>{formatCurrency(row.originalAmount)}</td>
                  <td style={{ textAlign: 'center' }}>
                    {onRetainedValueChange && (
                      <NumberInput
                        key={`retainedAmount-${row.key}`}
                        name="retainedAmount"
                        value={row.retainedAmount}
                        onChange={({ retainedAmount }) => {
                          onRetainedValueChange({
                            id: row.id,
                            key: row.key,
                            amount: retainedAmount,
                          });
                        }}
                        style={{
                          formInput: {
                            minWidth: '100px',
                            maxWidth: '157px',
                            margin: 'auto',
                          },
                          input: {
                            textAlign: 'right',
                          },
                        }}
                        icon={<Icon class={'fas-dollar-sign'} />}
                        min={0}
                        step={0.01}
                        roundOnBlur
                      />
                    )}
                    {!onRetainedValueChange && formatCurrency(row.retainedAmount)}
                  </td>
                  <td style={{ textAlign: 'center' }}>{formatCurrency(row.refundedAmount)}</td>
                </tr>
              );
            }}
          />
        </tbody>
        <tfoot>
          <tr>
            <td>{lang.total.toLocaleUpperCase()}</td>
            <td style={{ textAlign: 'center' }}>{formatCurrency(data.totalOriginalAmount)}</td>
            <td
              style={{
                textAlign: 'center',
                color: hasRetainedValueChanged ? Theme.Color.error : undefined,
              }}
            >
              {formatCurrency(data.totalRetainedAmount < 0 ? 0 : data.totalRetainedAmount)}
              {hasRetainedValueChanged && (
                <>
                  <br />
                  {lang.originalAmount}: {formatCurrency(originalRetainedTotal)}
                </>
              )}
            </td>
            <td style={{ textAlign: 'center' }}>{formatCurrency(data.totalRefundedAmount)}</td>
          </tr>
        </tfoot>
      </table>
    </>
  );
};
